import React, { CSSProperties, useLayoutEffect, useRef, useState } from "react";
import { useComponentLogger } from "../../shared/logging";
import { InteractiveElementProps } from "../../shared/props";
import { MenuIconButton, MenuIconButtonProps, MenuItem } from "../Buttons/MenuIconButton/MenuIconButton";
import { Stack } from "../Stacks/Stack/Stack";
import { StopPropagation } from "../StopPropagation/StopPropagation";

// Welcome to FlexibleMenu, a general solution for overflowing child elements into a contextual menu. Before digging into
// the code and comments, this summary may help:
//
//          FlexibleMenu ----> Aware of resize, refreshes OverflowArea when needed
//          OverflowArea ----> Overflows what is given to it on load and then does nothing else
//
// The separation of concerns keeps OverflowArea focused on overflow logic. Part of that logic is hiding elements from
// the DOM. To bring them back, OverflowSet will signal OverflowArea to update based on the `width` dependency in
// useLayoutEffect, and the overflow will be rechecked. This checking inside a useLayoutEffects prevents flickering
// from being painted in the browser.

export interface FlexibleMenuProps extends InteractiveElementProps {
    /** Options and existing menu items for overflow menu at the end. */
    readonly menuProps?: Omit<MenuIconButtonProps, "iconName">;

    /** Static height for the container. This value should be the tallest possible height an intended child can have. */
    readonly height: string | number;

    /** Aligment for the child elements. Defaults to flex-start. */
    readonly alignment?: "flex-start" | "center" | "flex-end";
}

function debounce(event: () => void, interval: number) {
    let timeoutId: NodeJS.Timeout;
    return () => {
        clearTimeout(timeoutId);
        timeoutId = global.setTimeout(event, interval);
    };
}

const safeIntervalForRefreshInMs = 300;

export const FlexibleMenu: React.FC<FlexibleMenuProps> = ({
    children,
    alignment = "flex-start",
    ...props
}) => {
    useComponentLogger("FlexibleMenu", props.id);

    const [windowWidth, setWindowWidth] = useState(1);

    const refreshOverflow = () => {
        // OverflowArea sets overflowed children as "hidden" in the DOM. To bring them back, we must force a refresh,
        // which is signalled to React with this prop.
        setWindowWidth(window.innerWidth);
    };

    useLayoutEffect(() => {
        // Resize events add up fast. To limit them and stay performant, we debounce the refresh.
        const debounceRefreshOverflow = debounce(refreshOverflow, safeIntervalForRefreshInMs);

        window.addEventListener("resize", debounceRefreshOverflow);
        return () => window.removeEventListener("resize", debounceRefreshOverflow);
    }, []);

    return (
        <OverflowArea windowWidth={windowWidth} alignment={alignment} {...props}>
            {children}
        </OverflowArea>
    );
};

interface OverflowAreaProps extends FlexibleMenuProps {
    /**
     *  This is the inner window width, which is not actually used in any calculations. It lets OverflowArea
     *  know when it should rerender itself. Example:
     *      1000 --> 1200 --> 1000
     *  At the end of a resize like this, React would see that the 1000 value is the same and it does not
     *  need to rerender. If however, it went like this:
     *      1000 --> 1100 --> 1150
     *  React would see 1150 is different than its previous value of 1000, causing a rerender and the
     *  useLayoutEffect to be run.
     */
    readonly windowWidth: number;

    /** Alignment for child elements. */
    readonly alignment: "flex-start" | "center" | "flex-end";
}

export const overflowMenuIconName = "MoreVertical";
const OverflowArea: React.FC<OverflowAreaProps> = ({
    menuProps,
    height,
    children,
    windowWidth,
    alignment,
    ...otherProps
}) => {
    // Inside OverflowArea, we have this flex container holding its children:
    const overflowContainer = useRef<HTMLDivElement>(null);

    const [isShowingMenu, setShowingMenu] = useState(false);

    // Holds the items that end up in the menu icon button
    const [overflowElements, setOverflowElements] = useState<MenuItem[]>([]);
    const existingMenuItems = menuProps?.menuItems || [];

    // The overflowContainer:
    // =======================================================
    // ||                                                   ||
    // ||  Child 1       Child 2        Child 3             ||
    // ||                                                   ||
    // =======================================================

    // This is great if they all happen to fit but we're concerned with overflow. The CSS properties below
    // cause the flex container to wrap child elements to lower rows. We then set a static height and
    // overflow:hidden, which hides all of the items on lower rows.
    const overflowAreaStyles: CSSProperties = {
        display: "flex",
        flexDirection: "row",
        justifyContent: alignment,
        flexWrap: "wrap",
        overflow: "hidden",
        flexShrink: 1,
        alignItems: "center",
        height,
        whiteSpace: "nowrap"
    };

    // =================
    // ||             ||
    // ||  Child 1    ||
    // ||             ||
    // =================
    //     Child 2
    //
    //     Child 3

    // Child 2 and 3 are visually hidden but still in the DOM. We need to obscure them from navigation and add them to the overflow menu

    // First, let's always bring back the menu on resize for accurate layout calculations.
    useLayoutEffect(() => {
        // This is in its own useLayoutEffect hook so that it is guaranteed to be done by the time the next one runs.
        setShowingMenu(true);
    }, [windowWidth, children, menuProps]);

    // Next, let's determine overflow.
    useLayoutEffect(() => {
        if (overflowContainer.current) {
            const overflowMenuItems: MenuItem[] = [];

            // Let's get the corresponding HTMLElements and React nodes of the children. HTML for measuring and hiding, React for adding to menu
            const htmlChildren = Array.from(overflowContainer.current.children) as HTMLElement[];
            const reactChildren = React.Children.toArray(children);

            // The height of the container
            const { clientHeight, offsetTop } = overflowContainer.current;
            const parentHeight = clientHeight + offsetTop;

            for (let index = 0; index < htmlChildren.length; index++) {
                const htmlElement = htmlChildren[index];
                if (htmlElement.hasAttribute("hidden")) {
                    htmlElement.removeAttribute("hidden");
                }
                // For every child, we are going to measure the distance from the top of itself to the inner border of the parent container.
                // Remember how Child 2 and Child 3 were outside the example container above due to the static height? This means that anything
                // on a row below the first will have an offsetTop greater than or equal to the parent container's height.

                // ==================
                // ||        ^^ ^  ||
                // ||  Child 1| |  ||   Parent container is 40px tall
                // ||         | |  ||
                // ===========|=|====
                //     Child 2| |
                //              |
                //     Child 3  |
                //
                // Each arrow is running from the child to the top of the parent container. OffsetTop values:
                //      Child 1: 10px
                //      Child 2: 40px
                //      Child 3: 52px

                // Child 2 and 3, therefore, belong in the overflow menu
                if (htmlElement.offsetTop >= parentHeight) {
                    const reactElement = reactChildren[index] as JSX.Element;
                    // Hide from navigation and screen readers
                    htmlElement.setAttribute("hidden", "");

                    // Add to overflow menu as MenuItem
                    overflowMenuItems.push({
                        key: `overflow-${index + 1}`,
                        name: `overflowItem-${index + 1}`,
                        "data-bi-id": reactElement.props["data-bi-id"] || "",
                        "aria-label": reactElement.props["aria-label"] || "",
                        onRender: () => reactElement
                    });
                }
            }

            // CSS leaves one last case to deal with. Suppose everything is overflowed and we are left with:

            // =========================
            // ||                     ||
            // ||  Child 1            ||
            // ||                     ||
            // =========================

            // What if Child 1 is wide enough to clip or go past the container? It won't wrap to the next row because it is the establishing
            // element of the first row. To handle this, after we already overflowed other items, we check just the width of
            // this first element versus its parent container.
            if (htmlChildren.length > 0) {
                const htmlElement = htmlChildren[0];
                const parentContainerWidth = overflowContainer.current.clientWidth;
                const firstElementWidth = htmlElement.clientWidth;

                // If it is too wide, we hide it from the DOM and add it to the overflow menu
                if (firstElementWidth >= parentContainerWidth) {
                    const reactElement = reactChildren[0] as JSX.Element;
                    htmlElement.setAttribute("hidden", "");

                    // Add to the front of the menu since this was the first item
                    overflowMenuItems.unshift({
                        key: "overflow-0",
                        name: "overflowItem-0",
                        "data-bi-id": reactElement.props["data-bi-id"] || "",
                        "aria-label": reactElement.props["aria-label"] || reactElement.props["ariaLabel"] || "",
                        onRender: () => reactElement
                    });
                }
            }

            // We may also already have menu items passed to OverflowArea. These need to be kept in the menu!
            const combinedOverflowMenuItems = [...overflowMenuItems, ...existingMenuItems];

            if (combinedOverflowMenuItems.length > 0) {
                setOverflowElements(combinedOverflowMenuItems);
            } else {
                // Nothing to show? Hide that menu
                setShowingMenu(false);
            }
        }
    }, [windowWidth, children, menuProps]);

    return (
        <Stack horizontal={true} verticalAlign={"center"} grow={true} horizontalAlign="end" padding={isShowingMenu ? "" : "0 8px 0 0"} >
            <StopPropagation grow={true}>
                <div style={overflowAreaStyles} ref={overflowContainer}>
                    {children}
                </div>
            </StopPropagation>
            {isShowingMenu && (
                <MenuIconButton
                    {...otherProps}
                    {...menuProps}
                    iconName={overflowMenuIconName}
                    menuItems={overflowElements}
                />
            )}
        </Stack>
    );
};
